import { Flex } from "@hackthenorth/north";
import React, { useEffect, useMemo, useState } from "react";
import { styled } from "twin.macro";

import { useSponsorPerksContext } from "src/views/sponsor/perks/SponsorPerksContext";
import {
  RELEASED_PERKS,
  PerkStatus,
  getPerkStatusIcon,
  normalizePerkStatus,
  PERK_TO_STATUS_SLUGS,
  PERKS_TO_DUE_DATES,
  PERKS_TO_LABELS,
  SponsorPerkType,
} from "src/views/sponsor/perks/types";

import Header from "../../../shared/components/Header";
import Onboarding from "../home/Onboarding";

import { PerkList, PerksProgressTracker, Legend } from "./components";
import Perk from "./components/Perk";

const SponsorPerksPage = () => {
  const { perks, refetchPerks } = useSponsorPerksContext();
  const [selectedPerk, setSelectedPerk] = useState<string | null>(null);
  const availablePerks = useMemo(
    () => perks?.available_perks.value ?? ([] as SponsorPerkType[]),
    [perks]
  );

  const filteredPerks = useMemo(
    () =>
      availablePerks
        .filter((p) => RELEASED_PERKS.includes(p as SponsorPerkType))
        .sort(
          (a, b) =>
            RELEASED_PERKS.indexOf(a as SponsorPerkType) -
            RELEASED_PERKS.indexOf(b as SponsorPerkType)
        ),
    [availablePerks]
  );

  useEffect(() => setSelectedPerk(filteredPerks[0]), [filteredPerks]);

  const perksByStatus = useMemo(
    () =>
      Object.values(PerkStatus).reduce(
        (obj, status) => ({
          ...obj,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          [status]: availablePerks.filter((type: any) => {
            const perkStatus =
              perks?.[PERK_TO_STATUS_SLUGS[type]]?.value ??
              PerkStatus.NOT_STARTED;
            return (
              perkStatus === status &&
              RELEASED_PERKS.includes(type as SponsorPerkType)
            );
          }),
        }),
        {}
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availablePerks]
  );

  const formattedPerks = useMemo(() => {
    return filteredPerks.map((perk: string) => {
      const status = normalizePerkStatus(
        perks?.[PERK_TO_STATUS_SLUGS[perk]]?.value as PerkStatus | undefined
      );
      const iconName = getPerkStatusIcon(status);

      return {
        perkSlug: perk,
        perkName: PERKS_TO_LABELS[perk],
        iconName,
        dueDate: perks?.[PERKS_TO_DUE_DATES[perk]]?.value,
        status: status ?? PerkStatus.INCOMPLETE,
      };
    });
  }, [filteredPerks, perks]);

  return (
    <>
      <Onboarding />
      <PageContainer>
        <HeaderWrapper>
          <Header
            title="Perks"
            subtitle="Submit information to get the most out of your sponsorship experience"
          />
        </HeaderWrapper>
        <TwoColumnContainer>
          <SecondaryCol>
            <SecondaryColContainer>
              <Legend />
              <PerksProgressTracker
                completed={perksByStatus?.[PerkStatus.COMPLETED]?.length ?? 0}
                totalPerks={filteredPerks.length || 1}
              />
              <PerkList
                perksArr={formattedPerks}
                updateSelectedPerk={(perk: string) => {
                  setSelectedPerk(perk);
                  refetchPerks();
                }}
                selectedPerk={selectedPerk}
              />
            </SecondaryColContainer>
          </SecondaryCol>
          <PrimaryCol>
            {selectedPerk && (
              <Perk key={selectedPerk} type={selectedPerk as SponsorPerkType} />
            )}
          </PrimaryCol>
        </TwoColumnContainer>
      </PageContainer>
    </>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const HeaderWrapper = styled.div`
  flex-shrink: 0;
`;

const TwoColumnContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
`;

const SecondaryCol = styled.div`
  width: 500px;
  border-right: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  height: 100%;
  overflow: hidden;
`;

const PrimaryCol = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.globalConstants.color.white};
  height: 100%;
  overflow: hidden;
`;

const SecondaryColContainer = styled(Flex).attrs({
  align: "center",
  column: true,
})`
  height: 100%;
  min-height: 0;
  padding: 36px 0 24px 0;
  overflow: hidden;
`;

export default SponsorPerksPage;
